/**
 * useScrollManagement Hook
 *
 * Manages scroll-related functionality including:
 * - Mouse wheel handling
 * - Keyboard scroll navigation
 * - Smooth scrolling
 * - Scroll position management
 * - Infinite scroll detection
 * - Throttled scroll events
 * - Container focus management
 */

import { useCallback, useEffect, useRef, useMemo } from 'react'
import { throttle } from 'lodash'

export const useScrollManagement = ({
  containerRef,
  isLoading = false,
  modalActive = false,
  onLoadMore,
  onScrollUp,
  onScrollDown,
  onWheel,
  throttleDelay = 500,
  scrollAmount = 200,
  backToTopAmount = 5000,
  infiniteScrollOffset = 5,
  enableInfiniteScroll = false,
  enableWheelHandling = true,
  enableContainerFocus = true
} = {}) => {
  // Internal refs for managing scroll state
  const wheelListenerRef = useRef(null)
  const scrollListenerRef = useRef(null)
  const loadMoreButtonRef = useRef(null)
  const currentCallbacksRef = useRef({})

  useEffect(() => {
    currentCallbacksRef.current = {
      onLoadMore,
      onScrollUp,
      onScrollDown,
      onWheel,
      isLoading,
      modalActive
    }
  }, [onLoadMore, onScrollUp, onScrollDown, onWheel, isLoading, modalActive])

  useEffect(() => {
    const loadMoreButton = document.getElementsByClassName('load-more-btn')[0]
    loadMoreButtonRef.current = loadMoreButton
  }, [])

  // Scroll direction utilities with smooth behavior detection
  const scrollUp = useCallback(
    (deltaY = scrollAmount) => {
      if (containerRef?.current) {
        containerRef.current.scrollTop -= deltaY
      } else {
        window.scrollBy({ top: -deltaY })
      }

      if (currentCallbacksRef.current.onScrollUp) {
        currentCallbacksRef.current.onScrollUp(deltaY)
      }
    },
    [containerRef, scrollAmount]
  )

  const scrollDown = useCallback(
    (deltaY = scrollAmount) => {
      if (containerRef?.current) {
        containerRef.current.scrollTop += deltaY
      } else {
        window.scrollBy({ top: deltaY })
      }

      if (currentCallbacksRef.current.onScrollDown) {
        currentCallbacksRef.current.onScrollDown(deltaY)
      }
    },
    [containerRef, scrollAmount]
  )

  // Back to top functionality with enhanced targeting
  const scrollToTop = useCallback(
    (customAmount = backToTopAmount) => {
      if (containerRef?.current) {
        containerRef.current.scrollTo({
          top: 0,
          behavior: 'smooth'
        })
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })
      }
    },
    [containerRef, backToTopAmount]
  )

  // Enhanced scroll to specific position
  const scrollToPosition = useCallback(
    (position, behavior = 'smooth') => {
      if (containerRef?.current) {
        containerRef.current.scrollTo({
          top: position,
          behavior
        })
      } else {
        window.scrollTo({
          top: position,
          behavior
        })
      }
    },
    [containerRef]
  )

  // Utility function for triggering DOM events
  const triggerEvent = useCallback((element, eventType, customEvent) => {
    if (!element) {
      console.error('Element does not exist:', element)
      return
    }
    const evt = customEvent ?? new Event(eventType, { bubbles: true })
    element.dispatchEvent(evt)
  }, [])

  // Wheel event handler with direction detection
  const handleWheel = useCallback(
    event => {
      if (!enableWheelHandling || !containerRef?.current) {
        if (currentCallbacksRef.current.onWheel) {
          currentCallbacksRef.current.onWheel(event)
        }
        return
      }

      const container = containerRef.current
      const { scrollTop, scrollHeight, clientHeight } = container
      const isAtTop = scrollTop === 0
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1

      // Only prevent default if we're not at natural scroll boundaries
      if (!isAtTop && !isAtBottom) {
        event.preventDefault()

        if (event.deltaY < 0) {
          // Scrolling up
          scrollUp(Math.abs(event.deltaY))
        } else if (event.deltaY > 0) {
          // Scrolling down
          scrollDown(event.deltaY)

          // Trigger load more if provided
          if (currentCallbacksRef.current.onLoadMore) {
            currentCallbacksRef.current.onLoadMore(event)
          }
        }
      }

      // Call external wheel handler
      if (currentCallbacksRef.current.onWheel) {
        currentCallbacksRef.current.onWheel(event)
      }
    },
    [enableWheelHandling, containerRef, scrollUp, scrollDown]
  )

  const handleInfiniteScroll = useCallback(() => {
    const { isLoading, modalActive, onLoadMore } = currentCallbacksRef.current

    let scrollTop, scrollHeight, clientHeight
    if (containerRef?.current) {
      const container = containerRef.current
      scrollTop = container.scrollTop
      scrollHeight = container.scrollHeight
      clientHeight = container.clientHeight
    } else {
      const docElement = document.documentElement
      scrollTop = docElement.scrollTop
      scrollHeight = docElement.scrollHeight
      clientHeight = docElement.clientHeight
    }

    // Check if we're near the bottom and not already loading
    if (
      scrollTop + clientHeight < scrollHeight - infiniteScrollOffset ||
      isLoading ||
      modalActive
    ) {
      return
    }

    if (loadMoreButtonRef.current) {
      triggerEvent(loadMoreButtonRef.current, 'click')
    }

    // Also call the callback if provided
    if (onLoadMore) {
      onLoadMore()
    }
  }, [containerRef, infiniteScrollOffset, triggerEvent])

  // Create throttled scroll handler
  const throttledScrollHandler = useMemo(
    () => throttle(handleInfiniteScroll, throttleDelay),
    [handleInfiniteScroll, throttleDelay]
  )

  // Container focus management
  const focusContainer = useCallback(() => {
    if (enableContainerFocus && containerRef?.current) {
      setTimeout(() => {
        if (containerRef?.current) {
          containerRef.current.focus()
        }
      }, 100)
    }
  }, [enableContainerFocus, containerRef])

  // Set up wheel event listeners
  useEffect(() => {
    if (!enableWheelHandling) return

    const targetElement = containerRef?.current || window

    if (targetElement) {
      wheelListenerRef.current = handleWheel
      targetElement.addEventListener('wheel', handleWheel, { passive: false })

      return () => {
        targetElement.removeEventListener('wheel', handleWheel)
      }
    }
  }, [handleWheel, enableWheelHandling, containerRef])

  // Set up infinite scroll listeners
  useEffect(() => {
    if (!enableInfiniteScroll) return

    const targetElement = containerRef?.current || window
    scrollListenerRef.current = throttledScrollHandler
    targetElement.addEventListener('scroll', throttledScrollHandler)

    return () => {
      targetElement.removeEventListener('scroll', throttledScrollHandler)
    }
  }, [enableInfiniteScroll, throttledScrollHandler, containerRef])

  // Focus container on mount
  useEffect(() => {
    if (enableContainerFocus) {
      focusContainer()
    }
  }, [enableContainerFocus, focusContainer])

  return {
    // Scroll utilities
    scrollUp,
    scrollDown,
    scrollToTop,
    scrollToPosition,

    // Event handlers
    handleWheel,
    handleInfiniteScroll,

    // Utilities
    triggerEvent,
    focusContainer,

    // Throttled handlers
    throttledScrollHandler
  }
}

export default useScrollManagement
